<script setup lang="ts">
import {ExtendedUser, Token} from "@/types";
import BaseLayout from "@/layouts/BaseLayout.vue";
import ProfileLayout from "@/Pages/settings/layouts/ProfileLayout.vue";
import TextField from "@/components/forms/TextField.vue";
import {Head, router, useForm} from "@inertiajs/vue3";
import {useDisplay} from "vuetify";
import {route} from "ziggy-js";
import axios from "axios";
import DisableTwoFactorAuthenticationDialog
    from "@/Pages/settings/authentication/components/DisableTwoFactorAuthenticationDialog.vue";
import {useFlashData} from "@/composables/flash";
import TokensList from "@/Pages/settings/authentication/components/TokensList.vue";
import InfoAlert from "@/components/alerts/InfoAlert.vue";

interface Props {
    user: ExtendedUser,
    twoFactorEnabled: boolean,
    tokens: Array<Token>
}

const props = defineProps<Props>()
defineOptions({
    layout: BaseLayout
})

const {smAndDown} = useDisplay()

const form = useForm({
    current_password: '',
    password: '',
    password_confirmation: '',
})

const submit = () => {
    form.put(route('user-password.update'), {
        errorBag: 'updatePassword',
        preserveScroll: true,
        onSuccess: () => {
            form.reset()
        }
    })
}

const setupTwoFactorAuthentication = () => {
    axios.post('/user/two-factor-authentication')
        .then(() => {
            router.visit(route('settings.two-factor-authentication'))
        })
}
</script>

<template>
    <Head title="Authentication" />
    <profile-layout
        :user="props.user"
    >
        <h1 class="font-weight-medium text-h5 mt-5">
            Personal access tokens
        </h1>
        <v-divider class="my-3" />
        <v-card
            :flat="true"
            class="mb-5"
        >
            <v-card-text>
                <v-row>
                    <span class="text-h5 black--text">Manage tokens</span>
                    <v-spacer />
                    <default-btn
                        class="border font-weight-medium mr-2"
                        rounded="lg"
                        @click="router.visit(route('settings.tokens.create'))"
                    >
                        <span class="font-weight-bold">
                            Generate new token
                        </span>
                    </default-btn>
                </v-row>
                <v-row>
                    <v-col
                        cols="12"
                        class="px-0"
                    >
                        <p>
                            Tokens you have generated that can be used to access the <a
                                href="#"
                                target="_blank"
                                class="text-primary"
                            >SendCrypt API</a>.
                        </p>
                        <info-alert
                            v-if="useFlashData.token"
                            class="my-2"
                        >
                            Make sure to copy your personal access token now. You won’t be able to see it again!
                        </info-alert>
                        <tokens-list
                            :user="props.user"
                            :tokens="props.tokens"
                            :temporary-token="useFlashData.token"
                        />
                    </v-col>
                    <v-col
                        cols="12"
                        class="px-0 pt-0"
                    >
                        <div class="text-caption text-disabled mb-3">
                            Personal access tokens can be used to authenticate to the API over Basic Authentication.
                        </div>
                    </v-col>
                </v-row>
            </v-card-text>
        </v-card>
        <h1 class="font-weight-medium text-h5 mt-15">
            Change password
        </h1>
        <v-divider class="my-5" />
        <v-form
            @submit.prevent="submit"
        >
            <v-row :class="smAndDown ? 'w-100' : 'w-50'">
                <v-col cols="12">
                    <text-field
                        v-model="form.current_password"
                        label="Old password"
                        :errors="form.errors.current_password"
                        type="password"
                    />
                    <text-field
                        v-model="form.password"
                        label="New password"
                        :errors="form.errors.password"
                        type="password"
                    />
                    <text-field
                        v-model="form.password_confirmation"
                        label="Confirm new password"
                        :errors="form.errors.password_confirmation"
                        type="password"
                    />
                    <legend class="text-caption text-grey-darken-2">
                        Make sure it's at least 9 characters including a number and a uppercase letter.
                    </legend>
                    <default-btn
                        :loading="form.processing"
                        class="border-sm"
                        rounded="lg"
                        type="submit"
                    >
                        <span class="font-weight-bold">
                            Update password
                        </span>
                    </default-btn>
                    <v-icon
                        v-if="form.wasSuccessful"
                        color="success"
                        class="ml-2"
                    >
                        mdi-check
                    </v-icon>
                </v-col>
            </v-row>
        </v-form>
        <h1 class="font-weight-medium text-h5 mt-15">
            Two-factor authentication
        </h1>
        <v-divider class="my-5" />
        <div>
            <div class="text-center pt-5">
                <v-icon>
                    mdi-lock-outline
                </v-icon>
            </div>
            <div class="text-h6 text-center font-weight-bold">
                Two-factor authentication is {{ props.twoFactorEnabled ? 'enabled' : 'not enabled yet' }}
            </div>
            <p class="text-center text-grey-darken-2">
                Two-factor authentication adds an additional layer of security to your account by requiring more than just a
                password to sign in.
            </p>
            <div class="text-center">
                <success-btn
                    v-if="!props.twoFactorEnabled"
                    class="mt-5"
                    border="sm"
                    rounded="lg"
                    @click="setupTwoFactorAuthentication"
                >
                    Enable two-factor authentication
                </success-btn>
                <disable-two-factor-authentication-dialog
                    v-if="props.twoFactorEnabled"
                />
            </div>
        </div>
    </profile-layout>
</template>

<style scoped>

</style>
